import { type ClassValue, clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

/**
 * Custom twMerge that recognizes our design system's fontSize utilities.
 * Without this, tailwind-merge treats text-tiny, text-body-sm, etc. as
 * textColor classes and strips them when a color class like text-gray-700 follows.
 */
// Custom color names from tailwind.config.ts that tailwind-merge doesn't
// recognize as standard Tailwind colors. Without this, classes like
// bg-primary-light and bg-teal-600 both survive in cn() instead of
// the latter overriding the former.
const customColors = [
  'primary', 'primary-light',
  'accent', 'accent-light',
  'success', 'success-light',
  'warning', 'warning-light',
  'error', 'error-light',
]

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        { text: ['micro', 'tiny', 'caption', 'body-sm', 'body', 'h4', 'h3', 'h2', 'h1', 'display'] },
      ],
      'bg-color': [{ bg: customColors }],
      'text-color': [{ text: customColors }],
      'border-color': [{ border: customColors }],
      'ring-color': [{ ring: customColors }],
    },
  },
})

/**
 * Utility for merging Tailwind CSS classes with proper conflict resolution.
 * Uses clsx for conditional classes and tailwind-merge to handle conflicts.
 *
 * @example
 * cn('px-4 py-2', 'px-6') // => 'py-2 px-6' (px-6 wins)
 * cn('text-red-500', isActive && 'text-blue-500')
 * cn(buttonVariants({ variant, size }), className)
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
